import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeArAe from '@angular/common/locales/ar-AE';
import localeEnGb from '@angular/common/locales/en-GB';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';

// Register Angular locales early so date/number/currency pipes resolve at
// bootstrap. ar-AE gives Arabic month/day names with Latin digits.
registerLocaleData(localeArAe);
registerLocaleData(localeEnGb);

/**
 * Cross-origin handoff consumer — pre-bootstrap.
 *
 * When the operator clicks "Switch to Local Hub" on the admin/cloud origin
 * (rms.ipossoft.io), that flow redirects to `http://<hub>/#handoff=<packed-auth>`.
 * On hub-origin cold load we want the POS shell to come up with an
 * already-hydrated session — no second PIN screen. We inline the decode
 * here so the handoff is consumed BEFORE StoreContextService /
 * PosStateService read localStorage.
 *
 * The cloud-side encoder lives in `HubHandoffService.buildRedirectUrl`. Keep
 * the two in sync — both use the v1 schema described in that service.
 */
(function consumeHandoff() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash ?? '';
  if (!hash.startsWith('#handoff=')) return;

  try {
    const encoded = hash.substring('#handoff='.length);
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const payload = JSON.parse(json);
    if (!payload || payload.v !== 1) return;

    // 2-minute TTL.
    const age = Date.now() - new Date(payload.issuedAtUtc).getTime();
    if (isNaN(age) || age < 0 || age > 120_000) return;

    const PREFIX = 'ipos_';
    const setRaw = (k: string, v: string) => { try { localStorage.setItem(PREFIX + k, v); } catch { /* quota */ } };
    const setObj = (k: string, v: unknown) => { try { localStorage.setItem(PREFIX + k, JSON.stringify(v)); } catch { /* quota */ } };

    if (payload.posToken)    setRaw('pos_token', payload.posToken);
    if (payload.posAuth)     setObj('pos_auth', payload.posAuth);
    if (payload.activeStore) setObj('active_store', payload.activeStore);
    if (payload.adminToken)  setRaw('token', payload.adminToken);
    if (payload.adminAuth)   setObj('auth', payload.adminAuth);
    // OF-FIX-RTC-1 — store code + device identity. Both are per-origin
    // state the handoff previously dropped, and both are load-bearing on
    // arrival: without the code an expired POS token bounces to the bare
    // `/pos` PIN screen with no store context ("No store selected"), and
    // without the device id every API call on the destination origin goes
    // out as the `web` sentinel, so writes and print jobs are attributed
    // to the store sentinel rather than this terminal.
    if (payload.posStandaloneCode) setRaw('pos_standalone_code', payload.posStandaloneCode);
    // NOT prefixed — DeviceIdentityService owns a bare `edgeClientId` key.
    if (payload.edgeClientId) {
      try { localStorage.setItem('edgeClientId', payload.edgeClientId); } catch { /* quota */ }
    }

    try { history.replaceState(null, '', window.location.pathname + window.location.search); }
    catch { /* no-op */ }

    // eslint-disable-next-line no-console
    console.info('[handoff] Session hydrated from cloud origin.');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[handoff] Consume failed:', err);
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* no-op */ }
  }
})();

/**
 * EDGE-DEV-ID — Persist the Flutter edge-client hardware device id BEFORE
 * Angular bootstraps so X-Edge-Client-Device-Id is correct on the very first
 * API call on EVERY route.
 *
 * The Flutter shell appends `?source=edgeclient&deviceId=<guid>` to the POS
 * URL it loads. We capture into sessionStorage here so the interceptor stops
 * falling back to the 'web' sentinel (which made bill/receipt routing resolve
 * to the wrong terminal).
 */
(function captureEdgeClientDeviceId() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const deviceId = (params.get('deviceId') ?? '').trim();
    if (deviceId) {
      // Own try: a sessionStorage failure must not skip the localStorage
      // reconcile below — the two stores fail independently.
      try {
        sessionStorage.setItem('edgeClientDeviceId', deviceId);
      } catch {
        /* sessionStorage unavailable — localStorage reconcile still applies */
      }
      // DEVICE-ID-CLONE-GAP — reconcile the durable store too. On a cloned
      // Windows image the WebView2 profile (and so localStorage) is copied
      // from the source machine, and localStorage wins the identity
      // precedence in DeviceIdentityService — so a stale first-device id
      // would silently shadow the fresh id the shell passes here, routing
      // every print/check to the first device. The shell re-appends
      // ?source=edgeclient&deviceId on every boot, so overwriting on
      // mismatch is always safe and self-heals cloned profiles.
      if (params.get('source') === 'edgeclient') {
        try {
          if (localStorage.getItem('edgeClientId') !== deviceId) {
            localStorage.setItem('edgeClientId', deviceId);
          }
        } catch {
          /* localStorage unavailable — in-memory/session paths still apply */
        }
      }
    }
  } catch {
    // sessionStorage unavailable — interceptor falls back to 'web'.
  }
})();

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));

// Register the app-shell service worker for cold-start offline (recovers the
// "dinosaur screen" when the terminal boots while WAN is down). Registered
// AFTER bootstrap so SW startup never blocks the Angular boot path. Skipped
// on local dev hosts so it doesn't intercept Vite HMR fetches.
const isLocalDevHost = typeof location !== 'undefined' && (
  location.hostname === 'localhost' ||
  location.hostname === '127.0.0.1' ||
  location.hostname === '0.0.0.0' ||
  location.hostname.endsWith('.local')
);

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  if (isLocalDevHost) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => {}));
    }).catch(() => {});
  } else {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/ipos-sw.js', { scope: '/' })
        .then((reg) => {
          // eslint-disable-next-line no-console
          console.info('[iPOS-SW] registered, scope =', reg.scope);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[iPOS-SW] registration failed:', err);
        });
    });
  }
}
